import {
	ReactElement,
	PropsWithChildren,
	useState,
	useContext,
	useEffect,
	useMemo,
	createContext,
	ReactNode,
	Context
} from "react";
import classNames from "classnames";
import {
	BusyIndicator,
	ContextActionBar,
	IconButtonWithTitle,
	PageContentTitle,
	Portal,
	ToolbarAction
} from "client/Components";
import {
	ensureNotNil,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import { flattenChildren, getReactNodeKey } from "client/Misc/reactUtils";
import { TitleBarBackButton } from "client/Features/Layout/TitleBarBackButton/TitleBarBackButton";
import styles from "./SettingsPage.module.less";
import { MediaQueryKind, useWorkspaceMediaQuery } from "client/Hooks";
import { localize } from "client_legacy/ndaw/culture";

type SectionContextData = {
	hasParentSection: false;
} | {
	hasParentSection: true;
	updateHasStretchChild(newValue: boolean): void;
};

const SectionContext: Context<SectionContextData> = createContext<SectionContextData>({
	hasParentSection: false
});

export type SettingsPageProps<TActionContext> = {
	hasBusyIndicator?: boolean;
	title: string;
	onMoreInfo?(): void;
	tagContent?: string;
} & (
	{
		actions?: undefined;
		actionContext?: undefined;
		hasSelection?: undefined;
	}
	|
	{
		actions: readonly ToolbarAction<TActionContext>[];
		actionContext: TActionContext;
		hasSelection?: boolean;
	}
) & (
	{
		onBack: () => void;
		backButtonText?: string;
	}
	|
	{
		onBack?: undefined;
		backButtonText?: undefined;
	}
);

export const SettingsPage = <TActionContext,>(props: PropsWithChildren<SettingsPageProps<TActionContext>>): ReactElement => {
	const {
		hasBusyIndicator,
		children,
		title,
		actionContext,
		actions,
		backButtonText = localize("_Back"),
		hasSelection = false,
		onBack,
		onMoreInfo,
		tagContent
	} = props;

	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const isResponsive: boolean = useWorkspaceMediaQuery(MediaQueryKind.Responsive);

	const shouldShowActionBar: boolean = (isMobile || hasSelection)
		&& isNotNil(actions);

	const busyIndicatorContainer: Element = useMemo(
		() => ensureNotNil(
			document.querySelector(".app-layout__body")
		),
		[]
	);

	return (
		<div className={styles.root}>
			{hasBusyIndicator && (
				<Portal container={busyIndicatorContainer}>
					<BusyIndicator />
				</Portal>
			)}
			{isNotNil(onBack) && (
				<TitleBarBackButton
					onBack={onBack}
				/>
			)}
			<header className={styles.header}>
				{isNotNil(onBack) && !isResponsive
					&& (
						<div className={styles.pageContentBackContainer}>
							<IconButtonWithTitle
								title={backButtonText}
								icon="chevron-left"
								onClick={onBack}
								trackingId="page-content-back"
							/>
						</div>
					)
				}
				{shouldShowActionBar && actions !== undefined && (
					<ContextActionBar
						actions={actions}
						context={actionContext}
						hasBorderBottom={isMobile}
						trackingId="global-action-bar"
						horizontalAlignment={isMobile ? "default" : "page-content"}
					/>
				)}
				{!isMobile && !hasSelection && (
					<PageContentTitle
						{...(
							actions !== undefined
								? { actions: actions, actionContext: actionContext }
								: { actions: undefined, actionContext: undefined }
						)}
						title={title}
						onMoreInfo={onMoreInfo}
						tagContent={tagContent}
					/>
				)}
			</header>

			<div className={styles.content}>
				{isMobile && isNotNil(title) && (
					<PageContentTitle
						title={title}
						onMoreInfo={onMoreInfo}
						tagContent={tagContent}
					/>
				)}
				{children}
			</div>
		</div>
	);
};
SettingsPage.displayName = "SettingsPage";

export type SettingsPageSectionProps = {
	title?: string;
	isVisible?: boolean;
	height?: "auto" | "stretch";
};

const Section = (props: PropsWithChildren<SettingsPageSectionProps>): ReactElement => {
	const {
		title,
		isVisible = true,
		height = "auto",
		children
	} = props;

	const childrenArray: ReadonlyArray<ReactNode> = useMemo(
		(): ReadonlyArray<ReactNode> => flattenChildren(children),
		[children]
	);

	const [childrenStretchStates, setChildrenStretchStates] = useState<ReadonlyArray<boolean>>([]);

	const hasAnyStretchChildren: boolean = useMemo(
		(): boolean => childrenStretchStates.some(x => x),
		[childrenStretchStates]
	);

	const context: SectionContextData = useContext(SectionContext);

	useEffect(
		() => {
			if (context.hasParentSection === true) {
				context.updateHasStretchChild(hasAnyStretchChildren || height === "stretch");
			}
		},
		[
			hasAnyStretchChildren,
			height,
			context
		]
	);

	const childrenContexts: ReadonlyArray<SectionContextData> = useMemo(
		(): ReadonlyArray<SectionContextData> => {
			return childrenArray.map((_, childIndex): SectionContextData => ({
				hasParentSection: true,
				updateHasStretchChild: (newValue: boolean): void => {
					setChildrenStretchStates(childrenStretchStates => {
						if (childrenStretchStates[childIndex] === newValue) {
							return childrenStretchStates;
						}

						const copy: boolean[] = [...childrenStretchStates];
						copy[childIndex] = newValue;

						return copy;
					});
				}
			}));
		},
		[childrenArray]
	);

	return (
		<section className={classNames(
			styles.section,
			{
				[styles.section_hidden]: !isVisible,
				[styles.section_stretch]: height === "stretch" || hasAnyStretchChildren
			}
		)}>
			{title &&
				<h3 className={styles.sectionTitle}>{title}</h3>
			}
			{childrenArray.map((child, index) => (
				<SectionContext.Provider
					key={getReactNodeKey(child, index)}
					value={childrenContexts[index]}
				>
					{child}
				</SectionContext.Provider>
			))}
		</section>
	);
};

Section.displayName = "SettingsPage.Section";

const SearchSection = (props: PropsWithChildren<{}>): ReactElement => {
	const {
		children
	} = props;
	return (
		<section
			{...setTrackingId("search-section")}
			className={styles.searchSection}
		>
			{children}
		</section>
	);
};

SearchSection.displayName = "SettingsPage.SearchSection";
SettingsPage.Section = Section;
SettingsPage.SearchSection = SearchSection;